/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useParams, Route, Prompt, useHistory } from 'react-router-dom';
import { Tabs, Tab, Modal, Button } from 'react-bootstrap/lib';
import { useAsync } from 'react-async-hook';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  QueryParamProvider,
  useQueryParam,
  StringParam,
  ArrayParam,
  BooleanParam,
} from 'use-query-params';
import { Dimmer, Loader } from 'semantic-ui-react';
import classnames from 'classnames';
import { Condition } from '@upstart/harmony/lib/legos/constants';
import { sendData } from 'util/amplitude';
import { amplitudeEventsPricing } from 'util/amplitudeEvents';
import DealerPriceRulesService from '../../services/DealerPriceRulesService';
import dealersActions from '../../redux/dealers/actions';
import userActions from '../../redux/user/actions';
import SidebarContainer from '../SidebarContainer';
import PricingMatrixEditor, {
  PricingMatrixEditorForUsed,
} from '../../components/PricingMatrixEditor';
import ErrorMessage from '../../components/ErrorMessage';
import SuccessMessage from '../../components/SuccessMessage';
import { getClientTypeName } from '../../util/constants';
import './PricingMatrix.scss';

const LEAVE_CONFIRM_MESSAGE =
  'Are you sure you want to go to another page? There are unsaved changes on this page.';

function PricingMatrixEmbeddedContainer({ children }) {
  return children;
}
PricingMatrixEmbeddedContainer.propTypes = {
  children: PropTypes.node,
};

function PricingMatrix({
  selectedDealer,
  userDealer,
  users,
  actions,
  embedded,
  embeddedStep,
  embeddedMaxSteps,
  clientType: propClientType,
  error,
  success,
  fetching,
}) {
  const history = useHistory();
  const { clientType: paramClientType } = useParams();
  const clientType = propClientType || paramClientType;
  const [condition, setCondition] = useQueryParam('condition', StringParam);
  const [years, onYearsChanged] = useQueryParam('years', ArrayParam);
  const [models, onModelsChanged] = useQueryParam('models', ArrayParam);
  const [onlyShowEnabled, onOnlyShowEnabledChanged] = useQueryParam(
    'onlyShowEnabled',
    BooleanParam,
  );

  const embeddedInDealerConfig = embedded && !!embeddedStep;
  const embeddedInCommandCenter = embedded && !embeddedStep;

  useEffect(() => {
    if (!embeddedInDealerConfig && userDealer.id) {
      actions.usersRequest(userDealer.id);
    }
  }, [userDealer.id]);

  const dealer = embeddedInDealerConfig ? selectedDealer : userDealer;
  const fetchDealerPriceRules = useAsync(DealerPriceRulesService.get, [
    dealer?.id,
  ]);
  const dealerPriceRules = fetchDealerPriceRules.result;

  const [arePriceRulesDirty, setArePriceRulesDirty] = useState(false);
  const [requestedCondition, setRequestedCondition] = useState();
  const [requestedLocation, setRequestedLocation] = useState();
  const [showLeavePageConfirmModal, setShowLeavePageConfirmModal] = useState(
    false,
  );
  const onConditionChanged = newCondition => {
    if (arePriceRulesDirty) {
      setRequestedCondition(newCondition);
      setRequestedLocation(undefined);
      setShowLeavePageConfirmModal(true);
    } else {
      setCondition(newCondition);
    }
  };
  const onLeavePageConfirmModalContinue = () => {
    if (requestedCondition) {
      setCondition(requestedCondition);
    }
    if (requestedLocation) {
      history.push(requestedLocation);
    }
    setShowLeavePageConfirmModal(false);
    setArePriceRulesDirty(false);
  };
  useEffect(() => {
    const callback = event => {
      if (arePriceRulesDirty) {
        event.preventDefault();
        event.returnValue = LEAVE_CONFIRM_MESSAGE;
      }
    };
    window.addEventListener('beforeunload', callback);
    return () => window.removeEventListener('beforeunload', callback);
  });
  const onBeforeNavigation = location => {
    if (location.pathname === history.location.pathname) return true;
    setShowLeavePageConfirmModal(true);
    setRequestedLocation(location);
    setRequestedCondition(undefined);
    return false;
  };

  const onSave = newDealerPriceRules => {
    setArePriceRulesDirty(false);
    const payload = { dealerPriceRules: newDealerPriceRules };
    actions.dealerPriceRulesUpdateRequest(payload);
  };

  const Container = embedded
    ? PricingMatrixEmbeddedContainer
    : SidebarContainer;

  const isFetching = fetching || fetchDealerPriceRules.loading;

  const sendAmplitudeEvent = (client, carCondition, action) => {
    sendData(amplitudeEventsPricing[client][carCondition][action]);
  };

  const activeTabKey = dealer?.isIndependent ? Condition.USED : condition;

  return (
    <Container title={`${getClientTypeName(clientType)} Pricing Matrix`}>
      <div
        className={classnames({
          'tabbed-page-container': true,
          'pricing-matrix-container': true,
          'pricing-matrix-container-embedded': embeddedInDealerConfig,
          'pricing-matrix-container-command-center': embeddedInCommandCenter,
        })}
      >
        <Modal show={showLeavePageConfirmModal}>
          <Modal.Header>
            <Modal.Title>Unsaved Changes</Modal.Title>
          </Modal.Header>
          <Modal.Body>{LEAVE_CONFIRM_MESSAGE}</Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setShowLeavePageConfirmModal(false)}>
              Cancel
            </Button>
            <Button bsStyle="danger" onClick={onLeavePageConfirmModalContinue}>
              Continue
            </Button>
          </Modal.Footer>
        </Modal>
        <Prompt
          when={arePriceRulesDirty && !showLeavePageConfirmModal}
          message={onBeforeNavigation}
        />
        {error && <ErrorMessage error={error} />}
        {success && <SuccessMessage success={success} />}
        <Dimmer active={isFetching} inverted>
          <Loader inverted content="Loading" />
        </Dimmer>
        <Tabs
          id="pricing-matrix-tabs"
          className="tabs"
          mountOnEnter
          unmountOnExit
          activeKey={activeTabKey || Condition.NEW}
          onSelect={c => onConditionChanged(c)}
        >
          {!dealer?.isIndependent && (
            <Tab eventKey={Condition.NEW} title="New">
              <PricingMatrixEditor
                dealer={dealer}
                dealerPriceRules={dealerPriceRules}
                users={users}
                clientType={clientType}
                condition={Condition.NEW}
                years={years}
                onYearsChanged={onYearsChanged}
                models={models}
                onModelsChanged={onModelsChanged}
                onlyShowEnabled={onlyShowEnabled}
                onOnlyShowEnabledChanged={onOnlyShowEnabledChanged}
                onChange={isReset => setArePriceRulesDirty(!isReset)}
                onSave={onSave}
                fetching={isFetching}
                sendAmplitudeEvent={sendAmplitudeEvent}
              />
            </Tab>
          )}
          <Tab eventKey={Condition.USED} title="Used">
            <PricingMatrixEditorForUsed
              dealer={dealer}
              dealerPriceRules={dealerPriceRules}
              users={users}
              clientType={clientType}
              condition={Condition.USED}
              onChange={isReset => setArePriceRulesDirty(!isReset)}
              onSave={onSave}
              fetching={isFetching}
              sendAmplitudeEvent={sendAmplitudeEvent}
            />
          </Tab>
          {!!dealer?.featureConfig?.useCPO?.enabled && !dealer?.isIndependent && (
            <Tab eventKey={Condition.CPO} title="Certified Pre Owned">
              <PricingMatrixEditor
                dealer={dealer}
                dealerPriceRules={dealerPriceRules}
                users={users}
                clientType={clientType}
                condition={Condition.CPO}
                years={years}
                onYearsChanged={onYearsChanged}
                models={models}
                onModelsChanged={onModelsChanged}
                onlyShowEnabled={onlyShowEnabled}
                onOnlyShowEnabledChanged={onOnlyShowEnabledChanged}
                onChange={isReset => setArePriceRulesDirty(!isReset)}
                onSave={onSave}
                fetching={isFetching}
                sendAmplitudeEvent={sendAmplitudeEvent}
              />
            </Tab>
          )}
        </Tabs>
      </div>
    </Container>
  );
}

PricingMatrix.propTypes = {
  actions: PropTypes.object,
  selectedDealer: PropTypes.object,
  userDealer: PropTypes.object,
  users: PropTypes.object,
  embedded: PropTypes.bool,
  embeddedStep: PropTypes.number,
  embeddedMaxSteps: PropTypes.number,
  clientType: PropTypes.string,
  error: PropTypes.any,
  success: PropTypes.any,
  fetching: PropTypes.bool,
};

const mapStateToProps = ({ dealers, user }) => ({
  selectedDealer: dealers.dealer,
  userDealer: user.currentUserDealer,
  users: user.users,
  fetching: dealers.fetching,
  success: dealers.success,
  error: dealers.error,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...dealersActions, ...userActions }, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(props => (
  <QueryParamProvider ReactRouterRoute={Route}>
    <PricingMatrix {...props} />
  </QueryParamProvider>
));
